/**
 * Branches Domain Seed Data
 *
 * Contains all seed data for Tenant Service (25) branch features:
 * - Business Branches (physical/logical locations)
 * - Business Licenses (company-level NMLS and state licenses)
 *
 * @see PR #246 - Partner Companies B2B Model
 */

import { seedId } from '../utils';
import { BUSINESS_IDS } from './tenants';

// =============================================================================
// BUSINESS BRANCH IDs
// =============================================================================

export const BUSINESS_BRANCH_IDS = {
  // Fiops Capital branches
  FUNDING_HEADQUARTERS: seedId('branch-funding-hq', 1),
  FUNDING_AUSTIN: seedId('branch-funding-austin', 2),
  FUNDING_DALLAS: seedId('branch-funding-dallas', 3),
  FUNDING_HOUSTON: seedId('branch-funding-houston', 4),

  // 1Sharpe Capital branches
  SHARPE_HEADQUARTERS: seedId('branch-sharpe-hq', 1),
  SHARPE_MIAMI: seedId('branch-sharpe-miami', 2),

  // Pacific Bridge Fund
  PACIFIC_HEADQUARTERS: seedId('branch-pacific-hq', 1),
} as const;

export type BusinessBranchId = (typeof BUSINESS_BRANCH_IDS)[keyof typeof BUSINESS_BRANCH_IDS];

// =============================================================================
// BUSINESS LICENSE IDs
// =============================================================================

export const BUSINESS_LICENSE_IDS = {
  // Fiops Capital licenses
  FUNDING_NMLS_COMPANY: seedId('license-funding-nmls', 1),
  FUNDING_STATE_TX: seedId('license-funding-tx', 2),
  FUNDING_STATE_FL: seedId('license-funding-fl', 3),
  FUNDING_STATE_CA: seedId('license-funding-ca', 4),
  FUNDING_STATE_AZ: seedId('license-funding-az', 5),

  // 1Sharpe Capital licenses
  SHARPE_NMLS_COMPANY: seedId('license-sharpe-nmls', 1),
  SHARPE_STATE_FL: seedId('license-sharpe-fl', 2),
  SHARPE_STATE_GA: seedId('license-sharpe-ga', 3),

  // Pacific Bridge Fund licenses
  PACIFIC_NMLS_COMPANY: seedId('license-pacific-nmls', 1),
  PACIFIC_STATE_CA: seedId('license-pacific-ca', 2),
} as const;

export type BusinessLicenseId = (typeof BUSINESS_LICENSE_IDS)[keyof typeof BUSINESS_LICENSE_IDS];

// =============================================================================
// DATA TYPES
// =============================================================================

export interface BranchAddressSeedData {
  street: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country?: string;
}

export interface BusinessBranchSeedData {
  id: string;
  businessId: string;
  name: string;
  code?: string;
  nmlsId?: string;
  address: BranchAddressSeedData;
  phone?: string;
  email?: string;
  isHeadquarters: boolean;
  isActive: boolean;
  managerId?: string;
  metadata?: Record<string, unknown>;
}

export interface BusinessLicenseSeedData {
  id: string;
  businessId: string;
  branchId?: string;
  licenseType: 'NMLS' | 'STATE' | 'DBA' | 'OTHER';
  licenseNumber: string;
  licenseName?: string;
  state?: string;
  status: 'ACTIVE' | 'PENDING' | 'EXPIRED' | 'SUSPENDED' | 'SURRENDERED';
  issuedAt?: string;
  expiresAt?: string;
  renewalDate?: string;
  verificationUrl?: string;
  notes?: string;
}

// =============================================================================
// BUSINESS BRANCH DATA
// =============================================================================

export const BUSINESS_BRANCHES: Record<keyof typeof BUSINESS_BRANCH_IDS, BusinessBranchSeedData> = {
  FUNDING_HEADQUARTERS: {
    id: BUSINESS_BRANCH_IDS.FUNDING_HEADQUARTERS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Headquarters',
    code: 'HQ',
    nmlsId: '123456',
    address: {
      street: '100 Financial Center Drive',
      street2: 'Suite 1500',
      city: 'Dallas',
      state: 'TX',
      zip: '75201',
      country: 'US',
    },
    phone: '(214) 555-1000',
    email: 'hq@opsmatrix.local',
    isHeadquarters: true,
    isActive: true,
    metadata: { floorCount: 2, parkingSpaces: 150 },
  },
  FUNDING_AUSTIN: {
    id: BUSINESS_BRANCH_IDS.FUNDING_AUSTIN,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Austin Regional Office',
    code: 'AUS',
    nmlsId: '123456-01',
    address: {
      street: '500 Congress Avenue',
      street2: 'Suite 800',
      city: 'Austin',
      state: 'TX',
      zip: '78701',
    },
    phone: '(512) 555-2000',
    email: 'austin@opsmatrix.local',
    isHeadquarters: false,
    isActive: true,
  },
  FUNDING_DALLAS: {
    id: BUSINESS_BRANCH_IDS.FUNDING_DALLAS,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Dallas Downtown',
    code: 'DFW',
    nmlsId: '123456-02',
    address: {
      street: '2100 Ross Avenue',
      city: 'Dallas',
      state: 'TX',
      zip: '75201',
    },
    phone: '(214) 555-3000',
    email: 'dallas@opsmatrix.local',
    isHeadquarters: false,
    isActive: true,
  },
  FUNDING_HOUSTON: {
    id: BUSINESS_BRANCH_IDS.FUNDING_HOUSTON,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    name: 'Houston Office',
    code: 'HOU',
    nmlsId: '123456-03',
    address: {
      street: '1000 Main Street',
      street2: 'Suite 2500',
      city: 'Houston',
      state: 'TX',
      zip: '77002',
    },
    phone: '(713) 555-4000',
    email: 'houston@opsmatrix.local',
    isHeadquarters: false,
    isActive: true,
  },
  SHARPE_HEADQUARTERS: {
    id: BUSINESS_BRANCH_IDS.SHARPE_HEADQUARTERS,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Headquarters',
    code: 'HQ',
    nmlsId: '789012',
    address: {
      street: '200 Peachtree Street',
      street2: 'Suite 3000',
      city: 'Atlanta',
      state: 'GA',
      zip: '30303',
    },
    phone: '(404) 555-1000',
    email: 'hq@1sharpe.local',
    isHeadquarters: true,
    isActive: true,
  },
  SHARPE_MIAMI: {
    id: BUSINESS_BRANCH_IDS.SHARPE_MIAMI,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    name: 'Miami Office',
    code: 'MIA',
    nmlsId: '789012-01',
    address: {
      street: '1450 Brickell Avenue',
      city: 'Miami',
      state: 'FL',
      zip: '33131',
    },
    phone: '(305) 555-2000',
    email: 'miami@1sharpe.local',
    isHeadquarters: false,
    isActive: true,
  },
  PACIFIC_HEADQUARTERS: {
    id: BUSINESS_BRANCH_IDS.PACIFIC_HEADQUARTERS,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    name: 'Headquarters',
    code: 'HQ',
    nmlsId: '456789',
    address: {
      street: '600 California Street',
      city: 'San Francisco',
      state: 'CA',
      zip: '94108',
    },
    phone: '(415) 555-1000',
    email: 'info@pacificbridge.local',
    isHeadquarters: true,
    isActive: true,
  },
};

// =============================================================================
// BUSINESS LICENSE DATA
// =============================================================================

export const BUSINESS_LICENSES: Record<keyof typeof BUSINESS_LICENSE_IDS, BusinessLicenseSeedData> = {
  FUNDING_NMLS_COMPANY: {
    id: BUSINESS_LICENSE_IDS.FUNDING_NMLS_COMPANY,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: 'NMLS',
    licenseNumber: '123456',
    licenseName: 'Fiops Capital LLC',
    status: 'ACTIVE',
    issuedAt: '2020-03-15',
    renewalDate: '2026-12-31',
    verificationUrl: 'https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/123456',
  },
  FUNDING_STATE_TX: {
    id: BUSINESS_LICENSE_IDS.FUNDING_STATE_TX,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    branchId: BUSINESS_BRANCH_IDS.FUNDING_HEADQUARTERS,
    licenseType: 'STATE',
    licenseNumber: 'TX-MBL-123456',
    licenseName: 'Texas Mortgage Banker License',
    state: 'TX',
    status: 'ACTIVE',
    issuedAt: '2020-03-15',
    expiresAt: '2026-12-31',
  },
  FUNDING_STATE_FL: {
    id: BUSINESS_LICENSE_IDS.FUNDING_STATE_FL,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: 'STATE',
    licenseNumber: 'FL-MBR-123456',
    licenseName: 'Florida Mortgage Broker License',
    state: 'FL',
    status: 'ACTIVE',
    issuedAt: '2021-06-01',
    expiresAt: '2026-12-31',
  },
  FUNDING_STATE_CA: {
    id: BUSINESS_LICENSE_IDS.FUNDING_STATE_CA,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: 'STATE',
    licenseNumber: 'CA-DRE-01234567',
    licenseName: 'California Real Estate Broker License',
    state: 'CA',
    status: 'ACTIVE',
    issuedAt: '2022-01-15',
    expiresAt: '2026-01-14',
    notes: 'Also authorized for mortgage activities under CRMLA',
  },
  FUNDING_STATE_AZ: {
    id: BUSINESS_LICENSE_IDS.FUNDING_STATE_AZ,
    businessId: BUSINESS_IDS.OPSMATRIX_CAPITAL,
    licenseType: 'STATE',
    licenseNumber: 'AZ-MB-0912345',
    licenseName: 'Arizona Mortgage Banker License',
    state: 'AZ',
    status: 'PENDING',
    notes: 'Application submitted December 2025',
  },
  SHARPE_NMLS_COMPANY: {
    id: BUSINESS_LICENSE_IDS.SHARPE_NMLS_COMPANY,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    licenseType: 'NMLS',
    licenseNumber: '789012',
    licenseName: '1Sharpe Capital LLC',
    status: 'ACTIVE',
    issuedAt: '2019-08-01',
    renewalDate: '2026-12-31',
  },
  SHARPE_STATE_FL: {
    id: BUSINESS_LICENSE_IDS.SHARPE_STATE_FL,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    branchId: BUSINESS_BRANCH_IDS.SHARPE_MIAMI,
    licenseType: 'STATE',
    licenseNumber: 'FL-MBL-789012',
    licenseName: 'Florida Mortgage Lender License',
    state: 'FL',
    status: 'ACTIVE',
    issuedAt: '2019-08-01',
    expiresAt: '2026-12-31',
  },
  SHARPE_STATE_GA: {
    id: BUSINESS_LICENSE_IDS.SHARPE_STATE_GA,
    businessId: BUSINESS_IDS.ONE_SHARPE_CAPITAL,
    branchId: BUSINESS_BRANCH_IDS.SHARPE_HEADQUARTERS,
    licenseType: 'STATE',
    licenseNumber: 'GA-ML-789012',
    licenseName: 'Georgia Mortgage Lender License',
    state: 'GA',
    status: 'ACTIVE',
    issuedAt: '2019-08-01',
    expiresAt: '2026-12-31',
  },
  PACIFIC_NMLS_COMPANY: {
    id: BUSINESS_LICENSE_IDS.PACIFIC_NMLS_COMPANY,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    licenseType: 'NMLS',
    licenseNumber: '456789',
    licenseName: 'Pacific Bridge Fund LLC',
    status: 'ACTIVE',
    issuedAt: '2021-01-15',
    renewalDate: '2026-12-31',
  },
  PACIFIC_STATE_CA: {
    id: BUSINESS_LICENSE_IDS.PACIFIC_STATE_CA,
    businessId: BUSINESS_IDS.PACIFIC_BRIDGE_FUND,
    branchId: BUSINESS_BRANCH_IDS.PACIFIC_HEADQUARTERS,
    licenseType: 'STATE',
    licenseNumber: 'CA-CFL-456789',
    licenseName: 'California Finance Lender License',
    state: 'CA',
    status: 'ACTIVE',
    issuedAt: '2021-01-15',
    expiresAt: '2026-12-31',
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getAllBusinessBranches(workspace?: string): BusinessBranchSeedData[] {
  const branches = Object.values(BUSINESS_BRANCHES);
  if (!workspace) return branches;
  return branches.map((b) => ({ ...b, name: `${b.name} [${workspace}]` }));
}

export function getAllBusinessLicenses(): BusinessLicenseSeedData[] {
  return Object.values(BUSINESS_LICENSES);
}

export function getBranchesByBusiness(businessId: string): BusinessBranchSeedData[] {
  return Object.values(BUSINESS_BRANCHES).filter((b) => b.businessId === businessId);
}

export function getLicensesByBusiness(businessId: string): BusinessLicenseSeedData[] {
  return Object.values(BUSINESS_LICENSES).filter((l) => l.businessId === businessId);
}

export function getLicensesByBranch(branchId: string): BusinessLicenseSeedData[] {
  return Object.values(BUSINESS_LICENSES).filter((l) => l.branchId === branchId);
}

export function getLicensesByState(state: string): BusinessLicenseSeedData[] {
  return Object.values(BUSINESS_LICENSES).filter((l) => l.state === state);
}

export function getActiveLicenses(): BusinessLicenseSeedData[] {
  return Object.values(BUSINESS_LICENSES).filter((l) => l.status === 'ACTIVE');
}

export function getHeadquartersBranches(): BusinessBranchSeedData[] {
  return Object.values(BUSINESS_BRANCHES).filter((b) => b.isHeadquarters);
}
